import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Gavel, Clock, Trophy, AlertCircle } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { trackReferralAttribution } from '../utils/referrals';

// =====================================================
// SUMMONS LANDING PAGE
// =====================================================

const SummonsLanding = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [summons, setSummons] = useState(null);
  const [trial, setTrial] = useState(null);
  const [summoner, setSummoner] = useState(null);
  const [error, setError] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [referralTracked, setReferralTracked] = useState(false);

  useEffect(() => {
    loadSummons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]); // loadSummons defined below, stable for this component

  // Countdown timer
  useEffect(() => {
    if (!summons?.expires_at) return;
    
    const interval = setInterval(() => {
      const now = Date.now();
      const expires = new Date(summons.expires_at).getTime();
      const remaining = Math.max(0, expires - now);
      
      if (remaining === 0) {
        setError('This summons has expired');
      }
      
      setTimeLeft(remaining);
    }, 1000);
    
    return () => clearInterval(interval);
  }, [summons]);

  useEffect(() => {
    if (!summons?.summoner_id || referralTracked) return;

    let cancelled = false;
    (async () => {
      try {
        await trackReferralAttribution({
          inviterUserId: summons.summoner_id,
          sourceType: 'jury_summon',
          sourceId: summons.id,
        });
        if (!cancelled) setReferralTracked(true);
      } catch (err) {
        if (!cancelled) {
          console.warn('⚠️ Failed to track summons referral attribution:', err?.message || err);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [summons?.id, summons?.summoner_id, referralTracked]);

  const loadSummons = async () => {
    try {
      // Load summons data
      const { data: summonsData, error: summonsError } = await supabase
        .from('summons')
        .select('*')
        .eq('summons_token', token)
        .single();
      
      if (summonsError) throw new Error('Invalid summons link');
      
      // Check if expired
      if (new Date(summonsData.expires_at) < new Date()) {
        await supabase
          .from('summons')
          .update({ status: 'expired' })
          .eq('id', summonsData.id);
        throw new Error('This summons has expired');
      }
      
      // Check if already accepted
      if (summonsData.status === 'accepted') {
        throw new Error('This summons has already been used');
      }
      
      setSummons(summonsData);

      // 📊 Track summons opened
      if (window.trackEvent) {
        window.trackEvent('summon_opened', {
          summons_id: summonsData.id,
          trial_id: summonsData.trial_id,
          summoner_id: summonsData.summoner_id
        });
      }

      // Load trial data
      const { data: trialData, error: trialError } = await supabase
        .from('trials')
        .select('*')
        .eq('id', summonsData.trial_id)
        .single();
      
      if (trialError) throw trialError;
      setTrial(trialData);
      
      // Load summoner data
      const { data: summonerData, error: summonerError } = await supabase
        .from('users')
        .select('username')
        .eq('id', summonsData.summoner_id)
        .single();
      
      if (summonerError) throw summonerError;
      setSummoner(summonerData);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const acceptSummons = async () => {
    setAccepting(true);
    
    try {
      // Mark summons as accepted
      await supabase
        .from('summons')
        .update({
          status: 'accepted',
          accepted_at: new Date().toISOString()
        })
        .eq('id', summons.id);
      
      // Update trial stats
      await supabase
        .from('trials')
        .update({ summons_accepted: (trial.summons_accepted || 0) + 1 })
        .eq('id', trial.id);
      
      // Navigate to trial with summons context
      navigate(`/?trial=${trial.id}&summons=${token}`);
      
    } catch (err) {
      console.error('Error accepting summons:', err);
      alert('Failed to accept summons');
    } finally {
      setAccepting(false);
    }
  };

  const formatTimeLeft = (ms) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-12 w-12 border-4 border-purple-500 border-t-transparent rounded-full mx-auto mb-4" />
          <div className="text-white font-bold">Loading summons...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-red-50 rounded-2xl p-8 text-center border-2 border-red-300">
          <AlertCircle className="mx-auto mb-4 text-red-500" size={48} />
          <h2 className="text-2xl font-black text-red-900 mb-2">Summons Expired</h2>
          <p className="text-red-700 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-purple-600 hover:bg-purple-700 text-white py-3 px-6 rounded-xl font-bold transition-all"
          >
            Go to GweffIT
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-orange-600 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        
        {/* Summons Card */}
        <div className="bg-white rounded-3xl p-8 shadow-2xl border-4 border-yellow-400">
          
          {/* Header */}
          <div className="text-center mb-6">
            <div className="text-6xl mb-4 animate-bounce">⚖️</div>
            <h1 className="text-4xl font-black text-gray-900 mb-2">
              YOU'VE BEEN SUMMONED
            </h1>
            <p className="text-xl text-gray-700">
              to serve as a juror on GweffIT
            </p>
          </div>

          {/* Time Remaining */}
          {timeLeft > 0 && (
            <div className="bg-orange-100 rounded-xl p-4 mb-6 border-2 border-orange-300">
              <div className="flex items-center justify-center gap-3">
                <Clock className="text-orange-600" size={24} />
                <div>
                  <div className="font-black text-2xl text-orange-900">
                    {formatTimeLeft(timeLeft)}
                  </div>
                  <div className="text-sm text-orange-700">
                    Bonus expires soon!
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Summoner */}
          <div className="bg-purple-50 rounded-xl p-4 mb-6 border-2 border-purple-200">
            <div className="text-center">
              <div className="text-sm font-bold text-purple-700 mb-1">
                SUMMONED BY
              </div>
              <div className="text-xl font-black text-purple-900">
                {summoner?.username || 'Anonymous Juror'}
              </div>
            </div>
          </div>

          {/* Trial Info */}
          <div className="bg-gray-50 rounded-xl p-6 mb-6">
            <div className="text-center mb-4">
              <div className="text-sm font-bold text-gray-600 mb-2">THE TRIAL</div>
              <div className="text-5xl mb-3">{trial?.image_emoji || '⚖️'}</div>
              <h2 className="text-2xl font-black text-gray-900 mb-3">
                {trial?.title}
              </h2>
            </div>
            
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="text-center p-3 bg-blue-100 rounded-lg">
                <div className="font-bold text-gray-900">{trial?.side_a}</div>
                <div className="text-sm text-gray-600">Option A</div>
              </div>
              <div className="text-center p-3 bg-red-100 rounded-lg">
                <div className="font-bold text-gray-900">{trial?.side_b}</div>
                <div className="text-sm text-gray-600">Option B</div>
              </div>
            </div>
            
            <div className="text-center text-sm text-gray-600">
              {trial?.total_votes || 0} votes cast • {trial?.jurors_filled || 0}/12 jurors
            </div>
          </div>

          {/* Rewards */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 mb-6 border-2 border-green-300">
            <div className="flex items-start gap-3">
              <Trophy className="text-green-600 flex-shrink-0 mt-1" size={24} />
              <div className="flex-1">
                <div className="font-black text-lg text-green-900 mb-2">
                  🎁 Vote Within 5 Minutes = Bonus Rewards
                </div>
                <div className="space-y-1 text-sm text-green-800">
                  <div>✓ <strong>You get:</strong> +100 coins bonus</div>
                  <div>✓ <strong>{summoner?.username} gets:</strong> +50 coins</div>
                  <div>✓ <strong>Plus:</strong> Standard +50 coins for voting</div>
                </div>
                <div className="mt-3 text-center font-black text-green-900 text-xl">
                  Total: +150 coins for you! 💰
                </div>
              </div>
            </div>
          </div>

          {/* Accept Button */}
          <button
            onClick={acceptSummons}
            disabled={accepting || timeLeft === 0}
            className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 disabled:from-gray-400 disabled:to-gray-500 text-white py-4 rounded-xl font-black text-xl transition-all transform hover:scale-105 disabled:scale-100 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {accepting ? (
              <>
                <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
                Accepting Summons...
              </>
            ) : timeLeft === 0 ? (
              'Bonus Expired - Vote Anyway'
            ) : (
              <>
                <Gavel size={24} />
                Accept Summons & Vote Now
              </>
            )}
          </button>

          <div className="text-center mt-4 text-xs text-gray-500">
            By accepting, you'll be taken to the trial to cast your vote
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <div className="text-white text-sm mb-2">
            Don't have an account? No problem - you'll get one automatically!
          </div>
          <button
            onClick={() => navigate('/')}
            className="text-white hover:text-yellow-300 font-bold underline"
          >
            or browse trials without accepting
          </button>
        </div>
      </div>
    </div>
  );
};

export default SummonsLanding;
